import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { toast } from 'sonner';

import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const PAGE_SIZE = 50;

export default function AuditLogsPage() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get('/audit-logs', { params: { page, limit: PAGE_SIZE } })).data); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Gagal memuat audit log.'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return <div className="animate-in-up">
    <div className="mb-6">
      <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><ScrollText className="w-6 h-6 text-[var(--fpccs-primary)]" /> Audit Log</h1>
      <p className="text-sm text-muted-fpccs mt-1">Jejak login dan perubahan data. Isi password dan request tidak pernah disimpan.</p>
    </div>
    <div className="card-fpccs overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>User</TableHead><TableHead>Aktivitas</TableHead><TableHead>Target</TableHead><TableHead>Status</TableHead><TableHead>IP</TableHead></TableRow></TableHeader>
        <TableBody>
          {loading ? [...Array(8)].map((_, index) => <TableRow key={index}>{[...Array(6)].map((__, cell) => <TableCell key={cell}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>) : data.items.length === 0 ? <TableRow><TableCell colSpan={6} className="py-12 text-center text-muted-fpccs">Belum ada aktivitas tercatat.</TableCell></TableRow> : data.items.map((item) => <TableRow key={item.id}>
            <TableCell className="text-xs whitespace-nowrap">{formatDate(item.created_at)}</TableCell>
            <TableCell><div className="text-xs">{item.actor_email || item.metadata?.email || 'Sistem'}</div><div className="text-[10px] text-muted-fpccs">{item.actor_role || '-'}</div></TableCell>
            <TableCell className="text-xs font-medium">{actionLabel(item.action)}</TableCell>
            <TableCell className="text-xs text-muted-fpccs max-w-[300px] truncate" title={item.path}>{item.method} {item.path}</TableCell>
            <TableCell><span className={`text-[11px] ${item.status_code < 400 ? 'text-emerald-400' : 'text-red-400'}`}>{item.status_code}</span></TableCell>
            <TableCell className="text-xs text-muted-fpccs">{item.ip_address}</TableCell>
          </TableRow>)}
        </TableBody>
      </Table>
    </div>
    <div className="mt-4 flex items-center justify-between text-xs text-muted-fpccs"><span>{data.total} aktivitas</span><div className="flex items-center gap-2"><Button size="icon" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}><ChevronLeft className="w-4 h-4" /></Button><span>Halaman {page} dari {totalPages}</span><Button size="icon" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}><ChevronRight className="w-4 h-4" /></Button></div></div>
  </div>;
}

function formatDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value));
}

function actionLabel(action) {
  const labels = {
    'auth.login_succeeded': 'Login berhasil',
    'auth.login_failed': 'Login gagal',
    'auth.login_blocked': 'Login akun nonaktif',
    'api.post': 'Membuat data',
    'api.put': 'Mengubah data',
    'api.patch': 'Mengubah data',
    'api.delete': 'Menghapus data',
  };
  return labels[action] || action;
}
